import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { fmtMoney, shortDate, todayISO, relativeTime } from "../lib/format";
import {
  Trash2,
  Plus,
  ArrowLeft,
  Wallet,
  ArrowLeftRight,
  CheckCircle2,
  Bell,
  Pencil,
  X,
} from "lucide-react";
import HelpButton from "../components/HelpButton";
import { Link } from "react-router-dom";
import { useAccounts } from "../lib/accounts";

const TYPE_META = {
  bill: { label: "Bill", sign: "-", bg: "var(--uncleared)", chipBg: "" },
  paycheck: { label: "Paycheck", sign: "+", bg: "var(--positive)", chipBg: "!bg-[#b5e4ca]" },
  transfer: { label: "Transfer", sign: "↔", bg: "#e5f0ff", chipBg: "!bg-[#a3d5ff]" },
};

export default function Recurring() {
  const [items, setItems] = useState([]);
  const [due, setDue] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const { accounts, selectedId } = useAccounts();
  // Default category by recurring type. Empty = no default (transfer).
  const defaultCategoryFor = (t) => (t === "bill" ? "Bills" : t === "paycheck" ? "Income" : "");
  const [form, setForm] = useState({
    name: "",
    amount: "",
    type: "bill",
    frequency: "monthly",
    next_date: todayISO(),
    category: "Bills",
    account_id: "",
    to_account_id: "",
  });

  useEffect(() => {
    if (!form.account_id && (selectedId || accounts[0]?.id)) {
      setForm((f) => ({ ...f, account_id: selectedId || accounts[0].id }));
    }
  }, [selectedId, accounts, form.account_id]);

  const load = async () => {
    try {
      const [r, d] = await Promise.all([api.get("/recurring"), api.get("/recurring/due")]);
      setItems(r.data);
      setDue(d.data);
    } catch (e) {
      console.error("Recurring load failed:", e?.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.type === "transfer" && !form.to_account_id) {
      return toast.error("Pick a destination account");
    }
    if (form.type === "transfer" && form.to_account_id === form.account_id) {
      return toast.error("From and To must be different");
    }
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        to_account_id: form.type === "transfer" ? form.to_account_id : null,
      };
      if (editingId) {
        await api.put(`/recurring/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/recurring", payload);
        toast.success("Added");
      }
      setEditingId(null);
      setForm((f) => ({
        ...f,
        name: "",
        amount: "",
        category: defaultCategoryFor(f.type),
        to_account_id: "",
      }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name,
      amount: String(r.amount),
      type: r.type,
      frequency: r.frequency,
      next_date: r.next_date,
      category: r.category || defaultCategoryFor(r.type),
      account_id: r.account_id,
      to_account_id: r.to_account_id || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm((f) => ({
      ...f,
      name: "",
      amount: "",
      type: "bill",
      frequency: "monthly",
      next_date: todayISO(),
      category: "Bills",
      to_account_id: "",
    }));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this scheduled item?")) return;
    await api.delete(`/recurring/${id}`);
    load();
  };

  const post = async (r) => {
    const label =
      r.type === "transfer" ? `transfer of ${fmtMoney(r.amount)}` : `${r.type} for ${fmtMoney(r.amount)}`;
    if (!window.confirm(`Post ${r.name} — ${label} today?`)) return;
    try {
      await api.post(`/recurring/${r.id}/post`);
      toast.success(`Posted "${r.name}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    }
  };

  const accountName = (id) => accounts.find((a) => a.id === id)?.name || "(unknown)";
  const otherAccounts = accounts.filter((a) => a.id !== form.account_id);

  return (
    <div className="space-y-4" data-testid="recurring-page">
      <div className="flex items-center justify-between">
        <Link to="/" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Bills & Paychecks</h1>
        <HelpButton id="recurring" />
      </div>

      {/* Due section */}
      {due.length > 0 && (
        <div
          className="nb-card-lg p-4 space-y-3"
          style={{ background: "#fdecc8" }}
          data-testid="due-section"
        >
          <div className="flex items-center gap-2">
            <Bell size={18} strokeWidth={3} />
            <div className="font-display font-black text-lg">
              Due to post ({due.length})
            </div>
          </div>
          <div className="text-xs font-medium text-[#4a4a4a]">
            These are past their next-date — tap ✓ to post the transaction now.
          </div>
          {due.map((r) => {
            const meta = TYPE_META[r.type] || TYPE_META.bill;
            return (
              <div
                key={r.id}
                data-testid={`due-item-${r.id}`}
                className="flex items-center justify-between gap-2 p-3 border-2 border-[#1a1a1a] rounded-xl bg-white"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-bold truncate">{r.name}</div>
                  <div className="text-[11px] font-medium text-[#4a4a4a]">
                    Was due {relativeTime(r.next_date)} · {meta.label}
                    {r.type === "transfer" &&
                      ` · ${accountName(r.account_id)} → ${accountName(r.to_account_id)}`}
                  </div>
                </div>
                <div className={`font-display font-black text-lg ${r.type === "paycheck" ? "balance-pos" : ""}`}>
                  {meta.sign}
                  {fmtMoney(r.amount)}
                </div>
                <button
                  data-testid={`post-due-${r.id}`}
                  onClick={() => post(r)}
                  className="nb-btn nb-btn-positive !py-2 !px-3"
                  title="Post this now"
                >
                  <CheckCircle2 size={16} strokeWidth={3} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <form onSubmit={submit} className="nb-card p-5 space-y-3" data-testid="recurring-form">
        {editingId && (
          <div
            data-testid="editing-banner"
            className="flex items-center justify-between p-2.5 border-2 border-[#1a1a1a] rounded-xl"
            style={{ background: "#fdecc8" }}
          >
            <div className="text-sm font-bold flex items-center gap-2">
              <Pencil size={14} strokeWidth={3} /> Editing "{form.name || "item"}"
            </div>
            <button
              type="button"
              data-testid="cancel-edit-btn"
              onClick={cancelEdit}
              className="nb-btn nb-btn-secondary !py-1 !px-2 !text-xs"
            >
              <X size={12} strokeWidth={3} /> Cancel
            </button>
          </div>
        )}
        <div className="grid grid-cols-3 gap-2">
          {["bill", "paycheck", "transfer"].map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`rtype-${t}`}
              onClick={() =>
                setForm((f) => {
                  // Swap category to the new type's default IF the current value
                  // is empty or was the previous type's auto-default (i.e. user
                  // hasn't manually customised it).
                  const prevDefault = defaultCategoryFor(f.type);
                  const shouldSwap = !f.category || f.category === prevDefault;
                  return {
                    ...f,
                    type: t,
                    category: shouldSwap ? defaultCategoryFor(t) : f.category,
                  };
                })
              }
              className={`chip !py-3 ${form.type === t ? `active ${TYPE_META[t].chipBg}` : ""}`}
            >
              {TYPE_META[t].label}
            </button>
          ))}
        </div>

        <div>
          <label className="label">Name</label>
          <input
            data-testid="rec-name"
            required
            className="nb-input"
            value={form.name}
            onChange={set("name")}
            placeholder={
              form.type === "bill"
                ? "Spotify, Phone…"
                : form.type === "paycheck"
                  ? "Part-time job"
                  : "Weekly savings"
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Amount</label>
            <input
              data-testid="rec-amount"
              type="number"
              step="0.01"
              required
              className="nb-input"
              value={form.amount}
              onChange={set("amount")}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="label">Frequency</label>
            <select
              data-testid="rec-frequency"
              className="nb-select"
              value={form.frequency}
              onChange={set("frequency")}
            >
              <option value="monthly">Monthly</option>
              <option value="biweekly">Biweekly</option>
              <option value="weekly">Weekly</option>
              <option value="one_time">One-time</option>
            </select>
          </div>
        </div>

        <div>
          <label className="label">
            {form.frequency === "one_time" ? "Date" : "Next date"}
          </label>
          <input
            data-testid="rec-next-date"
            type="date"
            required
            className="nb-input"
            value={form.next_date}
            onChange={set("next_date")}
          />
        </div>

        {accounts.length > 0 && (
          <div data-testid="rec-account-picker">
            <label className="label">
              {form.type === "transfer" ? "From account" : "Account this affects"}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {accounts.map((a) => (
                <button
                  type="button"
                  key={a.id}
                  data-testid={`rec-account-${a.name}`}
                  onClick={() => setForm({ ...form, account_id: a.id })}
                  className={`chip !py-1 !text-xs flex items-center gap-1 ${
                    form.account_id === a.id ? "active" : ""
                  }`}
                  style={form.account_id === a.id ? { background: a.color } : {}}
                >
                  <Wallet size={10} strokeWidth={3} /> {a.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {form.type === "transfer" && (
          <div data-testid="rec-to-account-picker">
            <label className="label">To account</label>
            <div className="flex flex-wrap gap-1.5">
              {otherAccounts.map((a) => (
                <button
                  type="button"
                  key={a.id}
                  data-testid={`rec-to-account-${a.name}`}
                  onClick={() => setForm({ ...form, to_account_id: a.id })}
                  className={`chip !py-1 !text-xs flex items-center gap-1 ${
                    form.to_account_id === a.id ? "active" : ""
                  }`}
                  style={form.to_account_id === a.id ? { background: a.color } : {}}
                >
                  <ArrowLeftRight size={10} strokeWidth={3} /> {a.name}
                </button>
              ))}
            </div>
            <div className="text-[11px] font-medium text-[#4a4a4a] mt-1">
              Money will move from{" "}
              <b>{accountName(form.account_id)}</b> to{" "}
              <b>{form.to_account_id ? accountName(form.to_account_id) : "(pick one)"}</b>.
            </div>
          </div>
        )}

        <button type="submit" data-testid="add-recurring-btn" className="nb-btn nb-btn-dark w-full !py-3">
          {editingId ? (
            <>
              <CheckCircle2 size={16} strokeWidth={3} /> Save changes
            </>
          ) : (
            <>
              <Plus size={16} strokeWidth={3} /> Add
            </>
          )}
        </button>
      </form>

      <div className="nb-card overflow-hidden">
        <div className="p-3 border-b-2 border-[#1a1a1a]/10">
          <div className="label !mb-0">Scheduled items</div>
        </div>
        {items.length === 0 && (
          <div className="p-6 text-sm text-center text-[#4a4a4a] font-medium">
            Nothing recurring yet.
          </div>
        )}
        {items.map((r) => {
          const meta = TYPE_META[r.type] || TYPE_META.bill;
          const isDue = due.some((d) => d.id === r.id);
          return (
            <div
              key={r.id}
              className="flex items-center justify-between py-3 px-4 border-b-2 border-[#1a1a1a]/10 last:border-0"
              data-testid={`recurring-item-${r.id}`}
              style={{ background: meta.bg }}
            >
              <div className="min-w-0 flex-1">
                <div className="font-bold truncate">{r.name}</div>
                <div className="text-xs text-[#4a4a4a] font-medium">
                  {r.frequency.replace("_", " ")} · next {shortDate(r.next_date)}
                  {r.type === "transfer" &&
                    ` · ${accountName(r.account_id)} → ${accountName(r.to_account_id)}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className={`font-display font-black text-lg ${r.type === "paycheck" ? "balance-pos" : ""}`}
                >
                  {meta.sign}
                  {fmtMoney(r.amount)}
                </div>
                {isDue && (
                  <button
                    data-testid={`post-item-${r.id}`}
                    onClick={() => post(r)}
                    className="nb-btn nb-btn-positive !py-1.5 !px-2"
                    title="Post now"
                  >
                    <CheckCircle2 size={14} strokeWidth={3} />
                  </button>
                )}
                <button
                  data-testid={`edit-recurring-${r.id}`}
                  onClick={() => startEdit(r)}
                  className="nb-btn nb-btn-secondary !py-1.5 !px-2"
                  title="Edit"
                >
                  <Pencil size={14} strokeWidth={3} />
                </button>
                <button
                  data-testid={`delete-recurring-${r.id}`}
                  onClick={() => remove(r.id)}
                  className="nb-btn nb-btn-danger !py-1.5 !px-2"
                >
                  <Trash2 size={14} strokeWidth={3} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
